import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import {
  Check, X, Edit2, Bot, Clock, MapPin, User, Megaphone,
  CheckCircle2, XCircle, AlertCircle, ChevronDown, ChevronUp,
} from "lucide-react";
import { PLATFORMS, US_STATES, getPlatformColor, getPlatformLabel } from "./constants";
import { PostDialog, type SocialPost } from "./PostDialog";

/* ── Patriotic tokens (matches Admin shell) ── */
const T = {
  navy:  "#002868",
  red:   "#B22234",
  gold:  "#C5A55A",
  green: "#2b5e49",
  bg:    "#FAF9F6",
  border: "rgba(27,27,27,0.12)",
  shadow: "0 2px 16px rgba(0,0,0,0.07)",
};
const sans = "'Instrument Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif";
const serif = "'Instrument Serif', Georgia, 'Times New Roman', serif";

/* ── Types ── */
interface PostRow {
  id: string;
  platform: string;
  geo_type: string | null;
  state_code: string | null;
  city_name: string | null;
  content: string;
  scheduled_at: string | null;
  status: string;
  ai_generated: boolean;
  denial_reason: string | null;
  notes: string | null;
  approved_by: string | null;
  approved_at: string | null;
  campaign_id: string | null;
  account_id: string | null;
  media_urls: string[];
  social_accounts: {
    username: string | null;
    handle: string | null;
    state_code: string | null;
  } | null;
  social_campaigns: {
    name: string;
  } | null;
}

type QueueFilter = "pending" | "approved" | "denied" | "all";

/* ── Stat card ── */
function StatCard({
  label, value, color, icon,
}: { label: string; value: number; color: string; icon: React.ReactNode }) {
  return (
    <div style={{
      backgroundColor: "#fff",
      border: `1px solid ${T.border}`,
      borderRadius: 10,
      padding: "16px 20px",
      display: "flex",
      alignItems: "center",
      gap: 14,
      boxShadow: T.shadow,
      flex: 1,
    }}>
      <div style={{
        width: 42,
        height: 42,
        borderRadius: "50%",
        backgroundColor: `${color}15`,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        color,
        flexShrink: 0,
      }}>
        {icon}
      </div>
      <div>
        <div style={{ fontSize: 26, fontWeight: 700, color, lineHeight: 1.1, fontFamily: sans }}>{value}</div>
        <div style={{ fontSize: 12, color: "rgba(27,27,27,0.62)", marginTop: 2, fontFamily: sans }}>{label}</div>
      </div>
    </div>
  );
}

/* ── Character meter ── */
function CharMeter({ content, platform }: { content: string; platform: string }) {
  const limit = PLATFORMS.find(p => p.id === platform)?.charLimit ?? 280;
  const count = content.length;
  const pct = Math.min(100, (count / limit) * 100);
  const over = count > limit;
  const color = over ? T.red : pct > 85 ? "#C5A55A" : T.green;
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 6 }}>
      <div style={{ flex: 1, height: 3, backgroundColor: "rgba(27,27,27,0.10)", borderRadius: 2, overflow: "hidden" }}>
        <div style={{ width: `${Math.min(100, pct)}%`, height: "100%", backgroundColor: color, borderRadius: 2, transition: "width 0.2s" }} />
      </div>
      <span style={{ fontSize: 11, color: over ? T.red : "rgba(27,27,27,0.45)", fontWeight: over ? 700 : 400, whiteSpace: "nowrap", fontFamily: sans }}>
        {count.toLocaleString()} / {limit.toLocaleString()}
      </span>
    </div>
  );
}

/* ── Post card ── */
function PostCard({
  post,
  onApprove,
  onDeny,
  onEdit,
  isPending,
}: {
  post: PostRow;
  onApprove: (id: string) => void;
  onDeny: (id: string, reason: string) => void;
  onEdit: (post: PostRow) => void;
  isPending: boolean;
}) {
  const [expanded, setExpanded] = useState(true);
  const [denyOpen, setDenyOpen] = useState(false);
  const [denyReason, setDenyReason] = useState("");

  const platformColor = getPlatformColor(post.platform);
  const displayColor = platformColor === "#000000" ? "#374151" : platformColor;
  const platformLabel = getPlatformLabel(post.platform);

  const isPending_ = post.status === "pending_approval";
  const isApproved = post.status === "approved";
  const isDenied   = post.status === "denied";

  const statusConfig = isPending_
    ? { color: "#C5A55A", bg: "#FDF8EE", label: "Pending Review", icon: <AlertCircle size={13} /> }
    : isApproved
    ? { color: T.green,  bg: "#EFF7F3", label: "Approved",       icon: <CheckCircle2 size={13} /> }
    : isDenied
    ? { color: T.red,    bg: "#FDF0F0", label: "Denied",         icon: <XCircle size={13} /> }
    : { color: "rgba(27,27,27,0.40)", bg: "#F5F5F5", label: post.status, icon: null };

  const geo = post.city_name
    ? `${post.city_name}${post.state_code ? `, ${post.state_code}` : ""}`
    : post.state_code ?? (post.geo_type === "national" ? "National" : "—");

  const accountLabel = post.social_accounts?.username ?? post.social_accounts?.handle ?? "No account";

  const handleDenySubmit = () => {
    onDeny(post.id, denyReason);
    setDenyOpen(false);
    setDenyReason("");
  };

  return (
    <div style={{
      backgroundColor: "#fff",
      border: `1px solid ${T.border}`,
      borderLeft: `4px solid ${displayColor}`,
      borderRadius: 10,
      overflow: "hidden",
      boxShadow: T.shadow,
      transition: "box-shadow 0.18s",
    }}>
      {/* Card header */}
      <div style={{
        padding: "14px 18px",
        display: "flex",
        alignItems: "flex-start",
        justifyContent: "space-between",
        gap: 12,
        borderBottom: `1px solid ${T.border}`,
        backgroundColor: "#FAFAF9",
      }}>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 6, alignItems: "center" }}>
          {/* Platform badge */}
          <span style={{
            display: "inline-flex", alignItems: "center",
            padding: "3px 10px", borderRadius: 20,
            backgroundColor: displayColor, color: "#fff",
            fontSize: 11, fontWeight: 700, letterSpacing: "0.03em",
            fontFamily: sans,
          }}>
            {platformLabel}
          </span>

          {/* Geo badge */}
          <span style={{
            display: "inline-flex", alignItems: "center", gap: 4,
            padding: "3px 9px", borderRadius: 20,
            backgroundColor: `${T.navy}12`, color: T.navy,
            fontSize: 11, fontWeight: 600, fontFamily: sans,
          }}>
            <MapPin size={10} />
            {geo}
          </span>

          {/* AI badge */}
          {post.ai_generated && (
            <span style={{
              display: "inline-flex", alignItems: "center", gap: 4,
              padding: "3px 9px", borderRadius: 20,
              backgroundColor: "#F0EEFF", color: "#5B4FBE",
              fontSize: 11, fontWeight: 600, fontFamily: sans,
            }}>
              <Bot size={10} />
              Claude
            </span>
          )}

          {/* Status badge */}
          <span style={{
            display: "inline-flex", alignItems: "center", gap: 4,
            padding: "3px 9px", borderRadius: 20,
            backgroundColor: statusConfig.bg, color: statusConfig.color,
            fontSize: 11, fontWeight: 600, fontFamily: sans,
          }}>
            {statusConfig.icon}
            {statusConfig.label}
          </span>
        </div>

        {/* Meta + collapse */}
        <div style={{ display: "flex", alignItems: "center", gap: 10, flexShrink: 0 }}>
          <div style={{ textAlign: "right" }}>
            {post.scheduled_at && (
              <div style={{ display: "flex", alignItems: "center", gap: 4, color: "rgba(27,27,27,0.45)", fontSize: 11, fontFamily: sans, justifyContent: "flex-end" }}>
                <Clock size={10} />
                {new Date(post.scheduled_at).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
                {" "}
                {new Date(post.scheduled_at).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" })}
              </div>
            )}
            {accountLabel !== "No account" && (
              <div style={{ display: "flex", alignItems: "center", gap: 4, color: "rgba(27,27,27,0.45)", fontSize: 11, fontFamily: sans, justifyContent: "flex-end", marginTop: 2 }}>
                <User size={10} />
                {accountLabel}
              </div>
            )}
            {post.social_campaigns && (
              <div style={{ display: "flex", alignItems: "center", gap: 4, color: "rgba(27,27,27,0.45)", fontSize: 11, fontFamily: sans, justifyContent: "flex-end", marginTop: 2 }}>
                <Megaphone size={10} />
                {post.social_campaigns.name}
              </div>
            )}
          </div>
          <button
            onClick={() => setExpanded(v => !v)}
            style={{ background: "none", border: "none", cursor: "pointer", color: "rgba(27,27,27,0.35)", padding: 4 }}
          >
            {expanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
          </button>
        </div>
      </div>

      {/* Content */}
      {expanded && (
        <>
          <div style={{ padding: "16px 18px" }}>
            <p style={{
              margin: 0,
              fontSize: 14,
              lineHeight: 1.65,
              color: "#1B1B1B",
              fontFamily: sans,
              whiteSpace: "pre-wrap",
            }}>
              {post.content}
            </p>
            <CharMeter content={post.content} platform={post.platform} />

            {/* Denial reason (if denied) */}
            {isDenied && post.denial_reason && (
              <div style={{
                marginTop: 12, padding: "10px 14px",
                backgroundColor: "#FDF0F0", borderRadius: 6,
                borderLeft: `3px solid ${T.red}`,
                fontSize: 12, color: T.red, fontFamily: sans,
              }}>
                <strong>Denial reason:</strong> {post.denial_reason}
              </div>
            )}

            {/* Approved info */}
            {isApproved && post.approved_at && (
              <div style={{
                marginTop: 12, padding: "10px 14px",
                backgroundColor: "#EFF7F3", borderRadius: 6,
                borderLeft: `3px solid ${T.green}`,
                fontSize: 12, color: T.green, fontFamily: sans,
              }}>
                ✓ Approved{post.approved_by ? ` by ${post.approved_by}` : ""} on {new Date(post.approved_at).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
              </div>
            )}

            {/* Deny reason input */}
            {denyOpen && (
              <div style={{ marginTop: 12, display: "flex", flexDirection: "column", gap: 8 }}>
                <Textarea
                  value={denyReason}
                  onChange={e => setDenyReason(e.target.value)}
                  placeholder="Reason for denial (optional but helpful for Claude to learn)…"
                  rows={2}
                  style={{ fontSize: 13, fontFamily: sans }}
                  autoFocus
                />
                <div style={{ display: "flex", gap: 8 }}>
                  <button
                    onClick={handleDenySubmit}
                    disabled={isPending}
                    style={{
                      flex: 1, padding: "9px 0", borderRadius: 7,
                      backgroundColor: T.red, color: "#fff",
                      border: "none", cursor: "pointer",
                      fontSize: 13, fontWeight: 700, fontFamily: sans,
                      opacity: isPending ? 0.6 : 1,
                    }}
                  >
                    Confirm Deny
                  </button>
                  <button
                    onClick={() => { setDenyOpen(false); setDenyReason(""); }}
                    style={{
                      padding: "9px 18px", borderRadius: 7,
                      backgroundColor: "transparent", color: "rgba(27,27,27,0.62)",
                      border: `1px solid ${T.border}`, cursor: "pointer",
                      fontSize: 13, fontFamily: sans,
                    }}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Action footer */}
          <div style={{
            padding: "12px 18px",
            borderTop: `1px solid ${T.border}`,
            backgroundColor: "#FAFAF9",
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-end",
            gap: 8,
          }}>
            {/* Edit */}
            <button
              onClick={() => onEdit(post)}
              style={{
                display: "flex", alignItems: "center", gap: 5,
                padding: "7px 14px", borderRadius: 7,
                backgroundColor: "transparent", color: "rgba(27,27,27,0.62)",
                border: `1px solid ${T.border}`, cursor: "pointer",
                fontSize: 12, fontWeight: 600, fontFamily: sans,
              }}
            >
              <Edit2 size={12} />
              Edit
            </button>

            {/* Deny button (only when pending or approved) */}
            {(isPending_ || isApproved) && !denyOpen && (
              <button
                onClick={() => setDenyOpen(true)}
                disabled={isPending}
                style={{
                  display: "flex", alignItems: "center", gap: 5,
                  padding: "7px 16px", borderRadius: 7,
                  backgroundColor: "#FDF0F0", color: T.red,
                  border: `1px solid rgba(178,34,52,0.22)`,
                  cursor: isPending ? "not-allowed" : "pointer",
                  fontSize: 12, fontWeight: 700, fontFamily: sans,
                  opacity: isPending ? 0.6 : 1,
                }}
              >
                <X size={13} />
                Deny
              </button>
            )}

            {/* Approve button */}
            {(isPending_ || isDenied) && (
              <button
                onClick={() => onApprove(post.id)}
                disabled={isPending}
                style={{
                  display: "flex", alignItems: "center", gap: 5,
                  padding: "7px 20px", borderRadius: 7,
                  backgroundColor: T.green, color: "#fff",
                  border: "none",
                  cursor: isPending ? "not-allowed" : "pointer",
                  fontSize: 12, fontWeight: 700, fontFamily: sans,
                  boxShadow: "0 2px 8px rgba(43,94,73,0.25)",
                  opacity: isPending ? 0.6 : 1,
                }}
              >
                <Check size={13} />
                Approve
              </button>
            )}
          </div>
        </>
      )}
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════ */
export function ApprovalQueue() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [queueFilter, setQueueFilter] = useState<QueueFilter>("pending");
  const [filterPlatform, setFilterPlatform] = useState("all");
  const [filterState, setFilterState] = useState("all");
  const [filterCampaign, setFilterCampaign] = useState("all");
  const [filterContentType, setFilterContentType] = useState("all");
  const [editingPost, setEditingPost] = useState<SocialPost | null>(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);

  /* Fetch posts with account + campaign info */
  const { data: posts = [], isLoading } = useQuery({
    queryKey: ["approval_queue", queueFilter, filterPlatform, filterState, filterCampaign, filterContentType],
    queryFn: async () => {
      let q = supabase
        .from("social_posts" as any)
        .select(`
          *,
          social_accounts (username, handle, state_code),
          social_campaigns (name)
        `)
        .order("scheduled_at", { ascending: true });

      if (queueFilter !== "all") q = q.eq("status", queueFilter === "pending" ? "pending_approval" : queueFilter);
      if (filterPlatform !== "all") q = q.eq("platform", filterPlatform);
      if (filterState !== "all") q = q.eq("state_code", filterState);
      if (filterCampaign !== "all") q = q.eq("campaign_id", filterCampaign);
      if (filterContentType !== "all") q = q.eq("content_type", filterContentType);

      const { data, error } = await q;
      if (error) throw error;
      return (data ?? []) as PostRow[];
    },
    refetchInterval: 30_000, // auto-refresh every 30s
  });

  /* Distinct content types from current posts (for filter dropdown) */
  const { data: contentTypes = [] } = useQuery({
    queryKey: ["distinct_content_types"],
    queryFn: async () => {
      const { data } = await supabase
        .from("social_posts" as any)
        .select("content_type")
        .not("content_type", "is", null);
      const set = new Set<string>();
      (data ?? []).forEach((r: any) => r.content_type && set.add(r.content_type));
      return Array.from(set).sort();
    },
  });

  /* Stats counts (always fetched regardless of filter) */
  const { data: stats } = useQuery({
    queryKey: ["approval_stats"],
    queryFn: async () => {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const todayIso = today.toISOString();

      const [pending, approvedToday, deniedToday] = await Promise.all([
        supabase.from("social_posts" as any).select("id", { count: "exact", head: true }).eq("status", "pending_approval"),
        supabase.from("social_posts" as any).select("id", { count: "exact", head: true }).eq("status", "approved").gte("approved_at", todayIso),
        supabase.from("social_posts" as any).select("id", { count: "exact", head: true }).eq("status", "denied").gte("updated_at", todayIso),
      ]);

      return {
        pending: pending.count ?? 0,
        approvedToday: approvedToday.count ?? 0,
        deniedToday: deniedToday.count ?? 0,
      };
    },
    refetchInterval: 30_000,
  });

  /* Approve / deny mutations */
  const updateMutation = useMutation({
    mutationFn: async ({ id, status, extra }: { id: string; status: string; extra?: Record<string, unknown> }) => {
      const updates: Record<string, unknown> = { status, ...extra };
      if (status === "approved") updates.approved_at = new Date().toISOString();
      const { error } = await supabase.from("social_posts" as any).update(updates).eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["approval_queue"] });
      queryClient.invalidateQueries({ queryKey: ["approval_stats"] });
      queryClient.invalidateQueries({ queryKey: ["social_posts"] });
    },
    onError: (e: Error) => toast({ title: "Update failed", description: e.message, variant: "destructive" }),
  });

  const saveMutation = useMutation({
    mutationFn: async (post: SocialPost) => {
      if (!post.id) return;
      const { id, ...updates } = post as SocialPost & { id: string };
      const { error } = await supabase.from("social_posts" as any).update(updates).eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["approval_queue"] });
      setEditDialogOpen(false);
      setEditingPost(null);
      toast({ title: "Post updated" });
    },
    onError: (e: Error) => toast({ title: "Save failed", description: e.message, variant: "destructive" }),
  });

  const handleApprove = (id: string) => updateMutation.mutate({ id, status: "approved" });
  const handleDeny = (id: string, reason: string) =>
    updateMutation.mutate({ id, status: "denied", extra: { denial_reason: reason || null } });

  const pendingPosts = posts.filter(p => p.status === "pending_approval");

  const handleBulkApprove = () => {
    if (!pendingPosts.length) return;
    if (!confirm(`Approve all ${pendingPosts.length} pending posts?`)) return;
    Promise.all(pendingPosts.map(p => updateMutation.mutateAsync({ id: p.id, status: "approved" }))).catch(() => {});
  };

  /* Fetch campaigns + accounts for edit dialog */
  const { data: campaigns = [] } = useQuery({
    queryKey: ["social_campaigns_list"],
    queryFn: async () => {
      const { data } = await supabase.from("social_campaigns" as any).select("id, name").order("name");
      return (data ?? []) as { id: string; name: string }[];
    },
  });
  const { data: accounts = [] } = useQuery({
    queryKey: ["social_accounts_list"],
    queryFn: async () => {
      const { data } = await supabase.from("social_accounts" as any).select("id, platform, username, handle, state_code").order("platform");
      return (data ?? []) as { id: string; platform: string; username: string | null; handle: string | null; state_code: string | null }[];
    },
  });

  /* Filter tabs */
  const FILTER_TABS: { id: QueueFilter; label: string; color: string }[] = [
    { id: "pending",  label: "Pending Review", color: "#C5A55A" },
    { id: "approved", label: "Approved",        color: T.green  },
    { id: "denied",   label: "Denied",          color: T.red    },
    { id: "all",      label: "All",             color: T.navy   },
  ];

  return (
    <div style={{ fontFamily: sans }}>
      {/* ── Stats row ── */}
      <div style={{ display: "flex", gap: 14, marginBottom: 24, flexWrap: "wrap" }}>
        <StatCard
          label="Pending Review"
          value={stats?.pending ?? 0}
          color="#C5A55A"
          icon={<AlertCircle size={20} />}
        />
        <StatCard
          label="Approved Today"
          value={stats?.approvedToday ?? 0}
          color={T.green}
          icon={<CheckCircle2 size={20} />}
        />
        <StatCard
          label="Denied Today"
          value={stats?.deniedToday ?? 0}
          color={T.red}
          icon={<XCircle size={20} />}
        />
      </div>

      {/* ── Filter row ── */}
      <div style={{
        backgroundColor: "#fff",
        border: `1px solid ${T.border}`,
        borderRadius: 10,
        padding: "14px 18px",
        marginBottom: 20,
        boxShadow: T.shadow,
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        flexWrap: "wrap",
        gap: 12,
      }}>
        {/* Status filter tabs */}
        <div style={{ display: "flex", gap: 0, border: `1px solid ${T.border}`, borderRadius: 8, overflow: "hidden" }}>
          {FILTER_TABS.map(ft => {
            const active = queueFilter === ft.id;
            return (
              <button
                key={ft.id}
                onClick={() => setQueueFilter(ft.id)}
                style={{
                  padding: "7px 16px",
                  border: "none",
                  borderRight: `1px solid ${T.border}`,
                  backgroundColor: active ? ft.color : "transparent",
                  color: active ? "#fff" : "rgba(27,27,27,0.62)",
                  fontSize: 12,
                  fontWeight: 600,
                  fontFamily: sans,
                  cursor: "pointer",
                  transition: "all 0.15s",
                  whiteSpace: "nowrap",
                }}
              >
                {ft.label}
                {ft.id === "pending" && (stats?.pending ?? 0) > 0 && (
                  <span style={{
                    marginLeft: 6,
                    backgroundColor: active ? "rgba(255,255,255,0.3)" : "#C5A55A",
                    color: active ? "#fff" : "#fff",
                    borderRadius: 10,
                    padding: "1px 6px",
                    fontSize: 10,
                    fontWeight: 700,
                  }}>
                    {stats?.pending}
                  </span>
                )}
              </button>
            );
          })}
        </div>

        {/* Platform + state + campaign + content_type + bulk approve */}
        <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
          <Select value={filterPlatform} onValueChange={setFilterPlatform}>
            <SelectTrigger className="h-8 w-36 text-xs"><SelectValue placeholder="All Platforms" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Platforms</SelectItem>
              {PLATFORMS.map(p => <SelectItem key={p.id} value={p.id}>{p.label}</SelectItem>)}
            </SelectContent>
          </Select>

          <Select value={filterState} onValueChange={setFilterState}>
            <SelectTrigger className="h-8 w-28 text-xs"><SelectValue placeholder="All States" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All States</SelectItem>
              {US_STATES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            </SelectContent>
          </Select>

          <Select value={filterCampaign} onValueChange={setFilterCampaign}>
            <SelectTrigger className="h-8 w-44 text-xs"><SelectValue placeholder="All Campaigns" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Campaigns</SelectItem>
              {campaigns.map(c => <SelectItem key={c.id} value={c.id}>{c.name.length > 30 ? c.name.slice(0, 30) + '…' : c.name}</SelectItem>)}
            </SelectContent>
          </Select>

          <Select value={filterContentType} onValueChange={setFilterContentType}>
            <SelectTrigger className="h-8 w-40 text-xs"><SelectValue placeholder="All Content Types" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Content Types</SelectItem>
              {contentTypes.map(ct => <SelectItem key={ct} value={ct}>{ct}</SelectItem>)}
            </SelectContent>
          </Select>

          {pendingPosts.length > 0 && queueFilter === "pending" && (
            <>
              <button
                onClick={handleBulkApprove}
                disabled={updateMutation.isPending}
                style={{
                  display: "flex", alignItems: "center", gap: 6,
                  padding: "7px 16px", borderRadius: 7,
                  backgroundColor: T.green, color: "#fff",
                  border: "none", cursor: "pointer",
                  fontSize: 12, fontWeight: 700, fontFamily: sans,
                  boxShadow: "0 2px 8px rgba(43,94,73,0.25)",
                }}
              >
                <Check size={13} />
                Approve Filtered ({pendingPosts.length})
              </button>
              <button
                onClick={() => {
                  if (!confirm(`Deny all ${pendingPosts.length} posts in current filter?`)) return;
                  Promise.all(pendingPosts.map(p =>
                    updateMutation.mutateAsync({ id: p.id, status: "denied", extra: { denial_reason: "bulk denied" } })
                  )).catch(() => {});
                }}
                disabled={updateMutation.isPending}
                style={{
                  display: "flex", alignItems: "center", gap: 6,
                  padding: "7px 16px", borderRadius: 7,
                  backgroundColor: T.red, color: "#fff",
                  border: "none", cursor: "pointer",
                  fontSize: 12, fontWeight: 700, fontFamily: sans,
                }}
              >
                <X size={13} />
                Deny Filtered
              </button>
            </>
          )}
        </div>
      </div>

      {/* ── Post list ── */}
      {isLoading ? (
        <div style={{ textAlign: "center", padding: "60px 0", color: "rgba(27,27,27,0.40)", fontFamily: sans, fontSize: 14 }}>
          Loading posts…
        </div>
      ) : posts.length === 0 ? (
        <div style={{
          textAlign: "center", padding: "64px 0",
          backgroundColor: "#fff", border: `1px solid ${T.border}`,
          borderRadius: 12, boxShadow: T.shadow,
        }}>
          <CheckCircle2 size={40} color={T.green} style={{ opacity: 0.4, marginBottom: 12 }} />
          <div style={{ fontFamily: serif, fontSize: 20, color: T.navy, marginBottom: 6 }}>
            {queueFilter === "pending" ? "Queue is clear" : "No posts found"}
          </div>
          <div style={{ fontSize: 13, color: "rgba(27,27,27,0.45)", fontFamily: sans }}>
            {queueFilter === "pending"
              ? "All AI-suggested posts have been reviewed. Check back when Claude drafts more."
              : "No posts match the selected filters."
            }
          </div>
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          {posts.map(post => (
            <PostCard
              key={post.id}
              post={post}
              onApprove={handleApprove}
              onDeny={handleDeny}
              onEdit={p => {
                setEditingPost(p as any);
                setEditDialogOpen(true);
              }}
              isPending={updateMutation.isPending}
            />
          ))}
        </div>
      )}

      {/* Edit dialog */}
      <PostDialog
        open={editDialogOpen}
        onOpenChange={setEditDialogOpen}
        post={editingPost}
        campaigns={campaigns}
        accounts={accounts as any}
        onSave={data => saveMutation.mutate(data)}
        isPending={saveMutation.isPending}
      />
    </div>
  );
}
